import { type ApolloClient, gql } from '@apollo/client/core';
import type { CreateHoldsMutationInput, Mutation, Query } from '../../../generated/api.graphql';
import type { VPError } from '../../../VPError';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as EventGroup from './EventGroup';
import * as EventGroupInput from './EventGroupInput';
import { HOLD_EVENT_GROUP_FIELDS } from './fragments';

export type CreateHoldsErrors = {
  eventGroupErrors?: VPError[];
  eventErrors?: VPError[][];
};

export const NEXT_HOLD_NUMBER_QUERY = gql`
  query GetNextHoldPreference($date: ISO8601Date!, $venueId: Int!, $roomId: Int!) {
    nextHoldNumber(date: $date, venueId: $venueId, roomId: $roomId)
  }
`;

export const CREATE_HOLDS_MUTATION = gql`
  mutation CreateEventHolds($input: CreateHoldsMutationInput!) {
    createHolds(input: $input) {
      success
      eventGroup {
        ...HoldEventGroupFields
      }
      eventGroupErrors {
        ...VPErrorFields
      }
      eventErrors {
        ...VPErrorFields
      }
    }
  }
  ${HOLD_EVENT_GROUP_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const getNextHoldPreference = async (
  client: ApolloClient<Query>,
  date: string,
  venueId: number,
  roomId: number,
): Promise<number> => {
  const { data } = await client.query<Query>({
    query: NEXT_HOLD_NUMBER_QUERY,
    variables: { date, venueId, roomId },
    fetchPolicy: 'network-only',
  });

  return data.nextHoldNumber;
};

export const createHolds = async (
  client: ApolloClient<Mutation>,
  params: EventGroupInput.EventGroupInput,
): Promise<VPResult<EventGroup.EventGroup, CreateHoldsErrors>> => {
  const input: CreateHoldsMutationInput = { createHolds: EventGroupInput.toGqlInput(params) };

  const { data } = await client.mutate<Mutation>({ mutation: CREATE_HOLDS_MUTATION, variables: { input } });

  const payload = data?.createHolds;

  if (!payload) {
    throw new Error('CreateEventHolds mutation returned no data');
  }

  if (payload.success) {
    if (!payload.eventGroup) {
      throw new Error('CreateEventHolds reported success without an event group');
    }

    return { success: true, data: EventGroup.fromGql(payload.eventGroup) };
  }

  return {
    success: false,
    errors: {
      eventGroupErrors: VPErrors.fromGqlCollection(payload.eventGroupErrors ?? []),
      eventErrors: (payload.eventErrors ?? []).map(errs => VPErrors.fromGqlCollection(errs ?? [])),
    },
  };
};
